import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewProps } from '@tiptap/react';
import React from 'react';
import type { Node as ProseMirrorNode, DOMOutputSpec } from '@tiptap/pm/model';

export interface EmbedConfig {
  name: string;
  attributes: Record<string, { default: unknown }>;
  component: React.FC<NodeViewProps>;
  tag: string;
  draggable?: boolean;
  selectable?: boolean;
  useNodeView?: boolean;
  parseHTMLGetAttrs?: (dom: HTMLElement) => Record<string, unknown>;
  renderHTMLAttributes?: (HTMLAttributes: Record<string, unknown>) => Record<string, unknown>;
  customRenderHTML?: (props: { HTMLAttributes: Record<string, unknown>; node: ProseMirrorNode }) => DOMOutputSpec;
  extraCommands?: Record<
    string,
    (
      embedName: string
    ) => (props: {
      commands: { deleteNode: (name: string) => boolean };
      state: { selection: { $from: { node: () => { type: { name: string } } } } };
    }) => boolean
  >;
}

function createEmbed({
  name,
  attributes,
  component,
  tag,
  draggable = false,
  selectable = true,
  useNodeView = true,
  parseHTMLGetAttrs,
  renderHTMLAttributes,
  customRenderHTML,
  extraCommands = {}
}: EmbedConfig) {
  return Node.create({
    name,
    group: 'block',
    atom: true,
    selectable,
    draggable,

    addAttributes() {
      return attributes;
    },

    parseHTML() {
      const parseConfig: { tag: string; getAttrs?: (dom: HTMLElement) => Record<string, unknown> } = { tag };
      if (parseHTMLGetAttrs) {
        parseConfig.getAttrs = parseHTMLGetAttrs;
      }
      return [parseConfig];
    },

    renderHTML({ HTMLAttributes, node }) {
      if (customRenderHTML) {
        return customRenderHTML({ HTMLAttributes, node });
      }

      const additionalAttributes = renderHTMLAttributes ? renderHTMLAttributes(HTMLAttributes) : {};

      return ['div', mergeAttributes(HTMLAttributes, additionalAttributes)] as DOMOutputSpec;
    },

    ...(useNodeView
      ? {
          addNodeView() {
            return ReactNodeViewRenderer(component);
          }
        }
      : {}),

    addCommands() {
      const commandName = `set${name.charAt(0).toUpperCase() + name.slice(1)}`;

      const processedExtraCommands: Record<
        string,
        (props: {
          commands: { deleteNode: (name: string) => boolean };
          state: { selection: { $from: { node: () => { type: { name: string } } } } };
        }) => boolean
      > = {};
      if (extraCommands) {
        Object.entries(extraCommands).forEach(([key, commandFn]) => {
          processedExtraCommands[key] = commandFn(name);
        });
      }

      return {
        [commandName]:
          (attrs: Record<string, unknown>) =>
          ({ commands }: { commands: { insertContent: (content: unknown) => boolean } }) =>
            commands.insertContent({ type: name, attrs }),
        ...processedExtraCommands
      };
    }
  });
}

export default createEmbed;
